import * as reportsSdk from '@adsk/acs-reports-sdk';
import get from 'lodash/get';
import T from 'i18n-react';
import { NotificationLevels, NotificationTypes } from '@adsk/bim360-matrix-react-components';
import {
  getReportsSdkConfig,
  getCurrentUser,
  getCurrentViewType,
  getOxygenMappings,
  getCurrentProject,
} from '../selectors';
import {
  createPdfReportStart,
  createPdfReportSuccess,
  createPdfReportFailure,
} from '../actions/reports-actions';
import { showNotificationAction, trackEvent } from '../actions';
import { downloadBlob } from '../../utils/downloadFile';
import {
  downloadReportAnalytics,
  downloadReportDelayed,
  downloadReportReadyForDownload,
  getAssigneeTypeByAssigneeId,
  getUserRole,
  viewTypeToScreenType,
  exportList,
} from '../../utils/analytics-helpers';
import featureFlags, { FEATURE_FLAGS } from '../../utils/featureFlags';
import { TIME_UNIT } from '../../constants/common';
import { diffInMS } from '../../utils/date-helpers';
import { reportFormats } from '../../constants/analytics';

export const createReport = (reportParams, reportMetaData = {}, failureMessage) => {
  return async (dispatch, getState) => {
    const { identifier } = reportMetaData;
    const { template, title } = reportParams;
    const state = getState();
    const requestConfig = getReportsSdkConfig(state);
    const { id: projectId } = getCurrentProject(state);

    const textExportSuccess = identifier
      ? T.translate('export.exportSinglePDFAsyncMessage', { identifier })
      : T.translate('export.exportSuccess', { reportTitle: title });
    const successMessageType = identifier ? NotificationLevels.INFO : NotificationLevels.SUCCESS;

    dispatch(createPdfReportStart());

    if (template === 'summary') {
      const viewType = getCurrentViewType(state);
      dispatch(trackEvent(exportList(reportFormats.PDF, viewType)));
    }

    return reportsSdk
      .createReport({ ...reportParams, projectId }, requestConfig)
      .then(() => {
        dispatch(createPdfReportSuccess());
        dispatch(
          showNotificationAction({
            type: NotificationTypes.TOAST,
            level: successMessageType,
            message: textExportSuccess,
          })
        );
      })
      .catch(() => {
        dispatch(createPdfReportFailure());
        dispatch(
          showNotificationAction({
            type: NotificationTypes.SNACKBAR,
            level: NotificationLevels.ERROR,
            message: failureMessage,
          })
        );
      });
  };
};

const trackDownloadReportEvent = ({ assignedTo, assignedToType, status }) => {
  return (dispatch, getState) => {
    const state = getState();
    const currentUser = getCurrentUser(state);
    const currentViewType = getCurrentViewType(state);
    const oxygen = getOxygenMappings(state);
    const currentUserWorkflowRoles = get(currentUser, 'attributes.rfis.workflowRoles');
    const downloadedBy = getUserRole(
      currentUserWorkflowRoles,
      currentUser.attributes.isProjectAdmin
    );
    const assignee = getAssigneeTypeByAssigneeId(
      assignedTo,
      currentUser.attributes.id,
      oxygen,
      assignedToType
    );
    const origin = viewTypeToScreenType(currentViewType);

    // Analytics - Send "Download RFI" event
    dispatch(trackEvent(downloadReportAnalytics(downloadedBy, assignee, origin, status)));
  };
};

export const createReportSync = (
  reportParams,
  { identifier, assignedTo, assignedToType, status, attachmentCount },
  fileName,
  asyncMessage,
  failureMessage
) => {
  return async (dispatch, getState) => {
    const state = getState();
    const requestConfig = getReportsSdkConfig(state);
    const { id: projectId } = getCurrentProject(state);
    const reportRequestStartTime = new Date();
    const synchronousReportTimeLimit =
      featureFlags.get(FEATURE_FLAGS.REPORT_SYNC_TIMEOUT_SECONDS) * TIME_UNIT.SECOND;

    dispatch(trackDownloadReportEvent({ assignedTo, assignedToType, status }));

    // Dispatch events for starting create report process and relevant notification message
    dispatch(createPdfReportStart());

    try {
      const { id } = await reportsSdk.createReport({ ...reportParams, projectId }, requestConfig);
      const pendingReport = reportsSdk.pollReport(
        {
          id,
          projectId,
          timeout: synchronousReportTimeLimit,
          skipNotificationOnComplete: true,
        },
        requestConfig
      );

      dispatch(
        showNotificationAction({
          type: NotificationTypes.TOAST,
          level: NotificationLevels.SUCCESS,
          message: asyncMessage,
          autoDismiss: 0.5,
          asyncOperation: pendingReport,
          closeButtonTooltipText: T.translate('notifications.downloadWillContinue'),
        })
      );

      const report = await pendingReport;

      if (report.status === 'complete') {
        const blob = await reportsSdk.downloadReport({ id, projectId }, requestConfig);
        dispatch(
          trackEvent(
            downloadReportReadyForDownload(
              diffInMS(reportRequestStartTime, new Date()),
              synchronousReportTimeLimit
            )
          )
        );
        downloadBlob(blob, fileName);
        dispatch(createPdfReportSuccess());
      } else {
        dispatch(createPdfReportFailure());
        dispatch(
          showNotificationAction({
            type: NotificationTypes.SNACKBAR,
            level: NotificationLevels.ERROR,
            message: failureMessage,
          })
        );
      }
    } catch (err) {
      if (err instanceof reportsSdk.PollDurationError) {
        dispatch(trackEvent(downloadReportDelayed(attachmentCount, synchronousReportTimeLimit)));
        const textExportSuccess = identifier
          ? T.translate('export.exportSinglePDFAsyncMessage', { identifier })
          : T.translate('export.exportSuccess', { reportTitle: reportParams.title });
        const successMessageType = identifier
          ? NotificationLevels.INFO
          : NotificationLevels.SUCCESS;

        dispatch(createPdfReportSuccess());
        dispatch(
          showNotificationAction({
            type: NotificationTypes.TOAST,
            level: successMessageType,
            message: textExportSuccess,
          })
        );
      } else {
        dispatch(createPdfReportFailure());
        dispatch(
          showNotificationAction({
            type: NotificationTypes.SNACKBAR,
            level: NotificationLevels.ERROR,
            message: failureMessage,
          })
        );
      }
    }
  };
};
